/**
 * Selection Component (Dropdown/Picker)
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { formatFromVariableSelect, formatFromVariableText } from '../../format-utils'
import type { VariableSelectValue, VariableTextValue } from '../../types'
import { deepMerge, parseToVariableSelect, parseToVariableText } from '../../parser-utils'
import selectionTemplate from './selection.template.json'

export interface ISelectionComponentSettings {
  // Data settings
  'selection-data-value': string // Column ID
  'selection-data-label': VariableTextValue
  'selection-data-default-value': VariableSelectValue
  'selection-data-default-value-editable': boolean
  'selection-data-options-source': string // Table ID for options
  'selection-data-options-label': string // Column ID for label
  'selection-data-options-value': string // Column ID for value
  'selection-data-options-icon': string // Column ID for icon
  'selection-data-options-filter': any
  'selection-data-options-sort-type': string
  'selection-data-separator': string

  // Design settings
  'selection-design-type': string // "dropdown" | "picker"
  
  // Behavior settings
  'selection-behavior-required'?: boolean
}

export interface ISelectionComponent extends IBuilderComponent {
  componentId: 'selection'
  groupId: 'form'
  settings: ISelectionComponentSettings
}

/**
 * Selection AI Input
 */
export interface AiSelectionInput {
  columnId: string // Required: maps to table column
  label?: string[] // Format: ["text:Select Option"] or ["text:Choose ", "cr:category"]
  defaultValue?: string // Default value in select format: "text:option1" or "cr:columnId"
  optionsSource?: string // Table ID for options
  optionsLabel?: string // Column ID for label
  optionsValue?: string // Column ID for value
  optionsIcon?: string // Column ID for icon
  selectionType?: 'dropdown' | 'radio' | 'chip' | 'segmented' // Selection type
  required?: boolean // Is this field required?
}

const SELECTION_DEFAULT_TEMPLATE = selectionTemplate as unknown as ISelectionComponentSettings

export class SelectionComp {
  getTemplate(): ISelectionComponentSettings {
    return SELECTION_DEFAULT_TEMPLATE
  }

  /**
   * format selection component settings to AI selection input
   */
  format(settings: ISelectionComponentSettings): AiSelectionInput {
    const result: AiSelectionInput = {
      columnId: settings['selection-data-value'],
    }

    // Label
    const label = formatFromVariableText(settings['selection-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Default Value
    const defaultValue = formatFromVariableSelect(settings['selection-data-default-value'])
    if (defaultValue) {
      result.defaultValue = defaultValue
    }

    // Options Source
    if (settings['selection-data-options-source']) {
      result.optionsSource = settings['selection-data-options-source']
    }

    // Options Label
    if (settings['selection-data-options-label']) {
      result.optionsLabel = settings['selection-data-options-label']
    }

    // Options Value
    if (settings['selection-data-options-value']) {
      result.optionsValue = settings['selection-data-options-value']
    }

    // Options Icon
    if (settings['selection-data-options-icon']) {
      result.optionsIcon = settings['selection-data-options-icon']
    }

    // Selection Type
    if (settings['selection-design-type']) {
      result.selectionType = settings['selection-design-type'] as 'dropdown' | 'radio' | 'chip' | 'segmented'
    }

    // Required
    if (settings['selection-behavior-required'] !== undefined) {
      result.required = settings['selection-behavior-required']
    }

    return result
  }

  /**
   * parse AI selection input to selection component settings
   */
  parse(input: AiSelectionInput): ISelectionComponentSettings {
    const result = deepMerge(SELECTION_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['selection-data-value'] = input.columnId
    }

    // Data - Label
    if (input.label) {
      result['selection-data-label'] = parseToVariableText(input.label)
    }

    // Data - Default Value
    if (input.defaultValue !== undefined) {
      result['selection-data-default-value'] = parseToVariableSelect(input.defaultValue)
    }

    // Data - Options Source
    if (input.optionsSource) {
      result['selection-data-options-source'] = input.optionsSource
    }

    if (input.optionsLabel) {
      result['selection-data-options-label'] = input.optionsLabel
    }

    if (input.optionsValue) {
      result['selection-data-options-value'] = input.optionsValue
    }

    if (input.optionsIcon) {
      result['selection-data-options-icon'] = input.optionsIcon
    }

    // Design - Type
    if (input.selectionType) {
      result['selection-design-type'] = input.selectionType
    }

    return result
  }
}
